import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import {
  mediaUnits,
  bulkEditEndpoint,
  bulkRecomputeLocationEndpoint,
  bulkDeleteEndpoint,
  mediaUnitsTagsEndpoints,
  availabilityWindows,
} from '@ooh/contracts';
import type {
  BulkEditRequest,
  BulkEditResponse,
  BulkRecomputeLocationRequest,
  BulkRecomputeLocationResponse,
  BulkDeleteRequest,
  BulkDeleteResponse,
  BulkMarkAvailabilityRequest,
  BulkMarkAvailabilityResponse,
} from '@ooh/contracts';
import { mediaUnitKeys, TAG_LIST_QUERY_KEY } from './queries';

// ─── Replace tags mutation ────────────────────────────────────

export interface ReplaceTagsVars {
  id: number;
  tagDefinitionIds: number[];
}

export function useReplaceTagsMutation() {
  const { auth } = useRouteContext({ from: '__root__' });
  const queryClient = useQueryClient();

  return useMutation<void, Error, ReplaceTagsVars>({
    mutationFn: ({ id, tagDefinitionIds }) =>
      auth.callWithAuth(mediaUnitsTagsEndpoints.replaceTags, { id, tagDefinitionIds }),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: mediaUnitKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: TAG_LIST_QUERY_KEY });
    },
  });
}

// ─── Bulk edit mutation ───────────────────────────────────────

export function useBulkEditMutation() {
  const { auth } = useRouteContext({ from: '__root__' });
  const queryClient = useQueryClient();

  return useMutation<BulkEditResponse, Error, BulkEditRequest>({
    mutationFn: (body) => auth.callWithAuth(bulkEditEndpoint, body),
    // BulkEditAction shows its own friendly, translated inline error on failure —
    // this no-op suppresses the global MutationCache fallback toast, which would
    // otherwise also fire with the raw backend message (see query-client.ts).
    onError: () => undefined,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mediaUnitKeys.lists() });
    },
  });
}

// ─── Bulk recompute location mutation ──────────────────────────

export function useBulkRecomputeLocationMutation() {
  const { auth } = useRouteContext({ from: '__root__' });
  const queryClient = useQueryClient();

  return useMutation<BulkRecomputeLocationResponse, Error, BulkRecomputeLocationRequest>({
    mutationFn: (body) => auth.callWithAuth(bulkRecomputeLocationEndpoint, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mediaUnitKeys.lists() });
    },
  });
}

// ─── Bulk delete mutation ─────────────────────────────────────

export function useBulkDeleteMutation() {
  const { auth } = useRouteContext({ from: '__root__' });
  const queryClient = useQueryClient();

  return useMutation<BulkDeleteResponse, Error, BulkDeleteRequest>({
    mutationFn: (body) => auth.callWithAuth(bulkDeleteEndpoint, body),
    // BulkDeleteAction shows its own translated inline error on failure — this
    // no-op suppresses the global MutationCache fallback toast (see query-client.ts).
    onError: () => undefined,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mediaUnitKeys.lists() });
    },
  });
}

// ─── Bulk mark availability mutation ──────────────────────────

export function useBulkMarkAvailabilityMutation() {
  const { auth } = useRouteContext({ from: '__root__' });
  const queryClient = useQueryClient();

  return useMutation<BulkMarkAvailabilityResponse, Error, BulkMarkAvailabilityRequest>({
    mutationFn: (body) => auth.callWithAuth(availabilityWindows.bulkMark, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mediaUnitKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: mediaUnitKeys.availabilityAll() });
    },
  });
}

// ─── Restore media unit from trash mutation ───────────────────

export function useRestoreMediaUnit() {
  const { auth } = useRouteContext({ from: '__root__' });
  const queryClient = useQueryClient();

  return useMutation<{ data: { id: number; grwId: string } }, Error, { id: number }>({
    mutationFn: ({ id }) => auth.callWithAuth(mediaUnits.restore, { id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mediaUnitKeys.trash() });
      void queryClient.invalidateQueries({ queryKey: mediaUnitKeys.lists() });
    },
  });
}
